/**
 * Wallet Address Display Component
 *
 * v3 redesign — shows wallet icon, truncated address, and copy button.
 * Uses v3 typography tokens and surface styling.
 */
import {
    CheckIcon,
    CopyIcon,
    WalletIcon
} from "@solomon-labs/design/components/icons";
import { Address } from "@solomon-labs/design/components/ui";
import React from "react";

import { addressTiersFor } from "./addressTiers";
import type { WalletAddressProps } from "./types";

/** Set in the `body` variant: 18px from `md`. */
const WALLET_ADDRESS_TIERS = addressTiersFor(18);

/**
 * Wallet address display with copy functionality
 */
export function WalletAddress({
    address,
    walletIcon,
    icon,
    onCopy,
    copySuccess = false
}: WalletAddressProps): React.ReactElement {
    return (
        <button
            onClick={onCopy}
            className="w-full flex items-center justify-between gap-2 bg-transparent border-none cursor-pointer py-2 group"
            aria-label={copySuccess ? "Copied!" : "Copy address"}
            title={copySuccess ? "Copied!" : "Copy address"}
        >
            <div className="flex min-w-0 flex-1 items-center gap-2">
                {icon ??
                    (walletIcon ? (
                        <img
                            src={walletIcon}
                            alt="Wallet"
                            className="w-6 h-6"
                        />
                    ) : (
                        <WalletIcon className="text-foreground-base size-5" />
                    ))}
                <Address
                    value={address}
                    tiers={WALLET_ADDRESS_TIERS}
                    textVariant="body"
                    // Our body face, not the element's default mono.
                    mono={false}
                    className="flex-1 text-left text-foreground-base"
                />
            </div>
            <div className="text-foreground-neutral group-hover:text-accent-base transition-colors duration-200">
                {copySuccess ? (
                    <CheckIcon className="size-6" />
                ) : (
                    <CopyIcon className="size-6" />
                )}
            </div>
        </button>
    );
}
